import React, { useRef, useState } from 'react';
import { Link } from 'react-router';

import flyLogo from '@fly/assets/images/fly_logo.svg';
import { Badge, IconButton, Tooltip } from '@fly/core';
import { downloadDesktopApp, getFirstLetter, routes } from '@fly/shared';
import { useClickOutside, useUser } from '@fly/shared-hooks';
import {
  selectIsChatOpen,
  toggleChat,
  useAppDispatch,
  useAppSelector,
} from '@fly/store';
import { FlySvgSprite } from '@fly/svg';
import { Avatar } from '@radix-ui/themes';
import classNames from 'classnames';
import log from 'loglevel';

import { AccountSubMenu, DesktopConnectionStatus } from '../index';
import styles from './header.module.less';

type HeaderProps = {
  isAccountRoute: boolean;
  isDesktopConnected: boolean;
};

export const Header = React.memo<HeaderProps>(
  ({ isAccountRoute = false, isDesktopConnected = false }) => {
    const subMenuRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    useClickOutside(subMenuRef, () => toggleMenu(false));
    useClickOutside(tooltipRef, () => toggleTooltip(false));

    const { username } = useUser();
    const [isOpen, toggleMenu] = useState(false);
    const [isTooltipOpen, toggleTooltip] = useState(false);
    const [isDownload, setDownloadState] = useState(false);
    const svgIcon = isDesktopConnected ? 'connect' : 'not_connect';

    const dispatch = useAppDispatch();
    const isChatOpen = useAppSelector(selectIsChatOpen);

    const handleClose = () => toggleMenu(false);
    const handleChatClick = () => dispatch(toggleChat());
    const handleMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      toggleTooltip(false);
      toggleMenu(!isOpen);
    };
    const handleConnectClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      toggleMenu(false);
      toggleTooltip(!isTooltipOpen);
    };

    const openDesktop = () => {
      setDownloadState(true);
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = `${import.meta.env.VITE_ELECTRON_APP_PROTOCOL}open`;
      document.body.appendChild(iframe);

      //App not install
      const timeout = setTimeout(() => {
        handleDownloadApp();
        setDownloadState(false);
        document.body.removeChild(iframe);
      }, 1500);

      window.addEventListener('blur', () => {
        setDownloadState(false);
        clearTimeout(timeout);
      });
    };
    const handleDownloadApp = async () => {
      setDownloadState(true);
      try {
        downloadDesktopApp();
      } catch (error) {
        log.error('Download failed:', error);
      } finally {
        setTimeout(() => {
          setDownloadState(false);
          toggleTooltip(false);
        }, 1500);
      }
    };

    return (
      <header className={styles.header} data-testid='app-header'>
        <div className={classNames(styles.left)}>
          <Link
            to={routes.DASHBOARD.ROOT}
            className={classNames(
              'align-center flex cursor-pointer items-center no-underline',
              isAccountRoute ? 'ml-3.75' : 'ml-fly-5'
            )}
          >
            <img
              src={flyLogo}
              alt='JFrog Fly'
              className={classNames('max-w-5.5')}
            />
            <FlySvgSprite
              className='ml-1'
              name='logo_text'
              size={68}
              height={28}
            />
            <Badge size='1' variant='soft' color='neutral' className='ml-fly-2'>
              Beta
            </Badge>
          </Link>
        </div>
        <div className={classNames(styles.right)}>
          <Tooltip
            ref={tooltipRef}
            open={isTooltipOpen}
            inset
            backgroundColor='var(--slate-5)'
            minWidth='228px'
            maxWidth='228px'
            sideOffset={5}
            key='tooltip-desktop-connection-status'
            content={
              <DesktopConnectionStatus
                startDownload={isDownload}
                isConnected={isDesktopConnected}
                downloadCallback={openDesktop}
              />
            }
            className={classNames(
              'align-center bg-slate-5 flex justify-center py-fly-4'
            )}
          >
            <IconButton
              color='neutral'
              variant={isTooltipOpen ? 'soft' : 'ghost'}
              onClick={handleConnectClick}
              icon={svgIcon}
            />
          </Tooltip>
          <IconButton
            color='neutral'
            variant={isChatOpen ? 'soft' : 'ghost'}
            onClick={handleChatClick}
            data-testid='chat-open-btn'
            className='text-(--fly-color-scheme-general-black)'
            icon='ai'
          />
          <IconButton
            color='accent'
            variant='ghost'
            onClick={handleMenuClick}
            data-testid='user-menu-trigger'
            icon={
              <Avatar
                fallback={getFirstLetter(username)}
                size='2'
                radius='full'
              />
            }
          />
        </div>
        {isOpen && <AccountSubMenu onNavClick={handleClose} ref={subMenuRef} />}
      </header>
    );
  }
);

Header.displayName = 'Header';
